import React, { useRef, useState } from "react";
import { Image as ImageIcon, Maximize2, Trash2, Upload } from "lucide-react";
import { uploadToCloudinary } from "../../../utility/uploadToCloudinary";
import { usePortfolioContext } from "./PortfolioContext";
import PortfolioMediaPreview, {
  PortfolioPreviewItem,
} from "./PortfolioMediaPreview";

const PortfolioImages: React.FC = () => {
  const { formData, setFormData } = usePortfolioContext();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedSlot, setSelectedSlot] = useState<number | "main" | null>(
    null,
  );
  const [uploadingSlot, setUploadingSlot] = useState<number | "main" | null>(
    null,
  );
  const [preview, setPreview] = useState<PortfolioPreviewItem | null>(null);

  const handleCardClick = (slot: number | "main") => {
    setSelectedSlot(slot);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || selectedSlot === null) return;

    setUploadingSlot(selectedSlot);
    try {
      const url = await uploadToCloudinary(file);

      if (selectedSlot === "main") {
        setFormData((prev) => ({ ...prev, mainImage: url }));
      } else {
        setFormData((prev) => {
          const newPhotos = [...(prev.photos || [])];
          newPhotos[selectedSlot as number] = url;
          return { ...prev, photos: newPhotos };
        });
      }
    } catch (err) {
      console.error("Upload failed", err);
    } finally {
      setUploadingSlot(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeImage = (slot: number | "main") => {
    if (slot === "main") {
      setFormData((prev) => ({ ...prev, mainImage: "" }));
    } else {
      setFormData((prev) => {
        const newPhotos = [...(prev.photos || [])];
        newPhotos.splice(slot, 1);
        return { ...prev, photos: newPhotos };
      });
    }
  };

  const renderSlot = (slot: number | "main") => {
    const isHero = slot === "main";
    const url = isHero
      ? formData.mainImage
      : formData.photos?.[slot as number];
    const label = isHero ? "Add cover image" : "Add image";
    const name = isHero ? "Cover image" : `Portfolio image ${slot}`;

    return (
      <div
        className={`portfolio-media-slot ${isHero ? "portfolio-media-slot-hero" : ""}`}
        key={String(slot)}
      >
        {url ? (
          <>
            <img src={url} alt={name} />

            {uploadingSlot === slot && (
              <div className="portfolio-media-slot-loading">
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Uploading...</span>
                </div>
              </div>
            )}

            <div className="portfolio-media-overlay">
              <button
                type="button"
                className="portfolio-media-action portfolio-media-action-primary"
                onClick={() => setPreview({ src: url, kind: "image", name })}
              >
                <Maximize2 size={17} />
                Preview
              </button>

              <button
                type="button"
                className="portfolio-media-action"
                onClick={() => handleCardClick(slot)}
              >
                <Upload size={17} />
                Replace
              </button>

              <button
                type="button"
                className="portfolio-media-action portfolio-media-action-danger"
                onClick={() => removeImage(slot)}
              >
                <Trash2 size={17} />
                Remove
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            className="portfolio-empty-upload"
            onClick={() => handleCardClick(slot)}
            disabled={uploadingSlot === slot}
          >
            <span className="portfolio-empty-icon">
              {uploadingSlot === slot ? (
                <span className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Uploading...</span>
                </span>
              ) : (
                <ImageIcon size={isHero ? 31 : 26} />
              )}
            </span>
            <strong>{uploadingSlot === slot ? "Uploading..." : label}</strong>
            <span>JPG, PNG, WEBP</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="portfolio-hidden-input"
        accept="image/*"
      />

      <section className="portfolio-showcase-card">
        <div className="portfolio-showcase-heading">
          <div>
            <span className="portfolio-eyebrow">FEATURED PORTFOLIO</span>
            <h2>Portfolio Images</h2>
            <p>
              Add one strong cover image and four supporting portfolio images.
              Hover or tap an uploaded image to preview, replace or remove it.
            </p>
          </div>
          <div className="portfolio-showcase-hint">
            <ImageIcon size={18} />
            5 featured slots
          </div>
        </div>

        <div className="portfolio-feature-grid">
          {renderSlot("main")}
          {[0, 1, 2, 3].map((index) => renderSlot(index))}
        </div>
      </section>

      <PortfolioMediaPreview
        preview={preview}
        onClose={() => setPreview(null)}
      />
    </>
  );
};

export default PortfolioImages;
